const challenge_overview = String.raw`
# Welcome to the Interspeech 2025 ML-SUPERB Challenge!

[![Slack][slack-badge]][slack-invite]

[slack-badge]: https://img.shields.io/badge/slack-chat-green.svg?logo=slack
[slack-invite]: https://join.slack.com/t/ml-superb/shared_invite/zt-2vwqsru8w-YQaEbAt_4A~nIH8CmDTLzA

## Pushing the state-of-the-art in multilingual ASR and LID
We present the ML-SUPERB 2.0 Interspeech 2025 Challenge, which aims to develop state-of-the-art Automatic Speech Recognition (ASR) and Language Identification (LID) models that are more inclusive of the world's languages and language varieties.
Building upon previous [SUPERB challenges](https://superbbenchmark.org), which have traditionally focused on evaluating self-supervised speech representations, this year's challenge is aimed at improving the performance and robustness of multilingual ASR and LID systems.
In particular, the original [ML-SUPERB Benchmark](https://www.isca-archive.org/interspeech_2023/shi23g_interspeech.pdf) and the [ML-SUPERB 2023 Challenge](https://ieeexplore.ieee.org/stamp/stamp.jsp?arnumber=10389628) encouraged participants to develop and benchmark state-of-the-art speech representations across various languages.
Instead, the ML-SUPERB 2.0 Challenge encourages the development of well-performing multilingual ASR and LID systems that can handle a diverse range of languages and dialects.
The design of the challenge aligns with the objectives of the [ML-SUPERB 2.0 benchmark](https://www.isca-archive.org/interspeech_2024/shi24g_interspeech.pdf), which encourages research on methods that leave no language ”behind”.

We also encourage participants to check out the [Faetar Challenge](https://perceptimatic.github.io/faetarspeech/), which focuses on a specific application of low-resource/dialectal ASR.

In summary, the challenge:

* Has the goal of developing SOTA systems for all languages and language varieties.
* Focuses on two tasks, namely multilingual LID and ASR.
* Evaluates on 154 unique languages, 200+ language varieties.
* Has a single track, evaluated by the average ranking across the following metrics:
    * Average LID accuracy across all 154 languages.
    * Average Character Error Rate (CER) across all 154 languages.
    * Average CER across the 15 worst-performing languages in the set of 154 languages.
    * Standard Deviation of CER computed for each of the 154 languages.
    * Average LID across the evaluated language varieties.
    * Average CER across the evaluated language varieties.
* Allows using any resource for data (both supervised and unsupervised).
* Allows almost any pre-trained model (see below). 
* Has an [online leaderboard and evaluation server](https://dynabench.org/tasks/ml-superb) (more information is given below).

## Slack workspace
Please join [our Slack workspace](https://join.slack.com/t/ml-superb/shared_invite/zt-2vwqsru8w-YQaEbAt_4A~nIH8CmDTLzA) for further discussions!

## Task
We invite participants to develop systems that both identify the language of a spoken utterance (LID) and transcribe the content of the speech into text (ASR). 
The list of languages that need to be supported can be found [here](https://gist.github.com/wanchichen/5c7f2598e27059accdef3704323a4e84). 
We note that for some utterances, we will also provide the identity of the language being spoken.
This is for the sake of running analyses on the effects of LID errors, and will not affect scoring.
As such, participants will provide the following $$API()$$ function as their submission.
~~~python=
    def API(waveform, true_lid=None):
        """
        Args:
            waveform (np.array): 16kHz 1D speech waveform
            true_lid (str): ISO3 code of true language id, or None

        Returns:
            pred_lid (str): ISO3 code of predicted language id
            pred_asr (str): predicted transcript in all caps, no punctuation
        """
    
        # the case where we do not know the language being spoken
        if true_lid is None:
            pred_lid = 
            pred_asr = 
        # we know the language being spoken
        else:
            pred_lid = true_lid
            pred_asr = 

        return pred_lid, pred_asr
~~~
Participants will fill in the API function to call their model(s) for LID prediction and/or ASR.
While the LID and ASR models can be completely independent, the LID prediction must be in lowercase and formatted using the ISO 639-3 language code enclosed in square brackets (e.g., [eng] for English).

## Data
**Participants are allowed to use any existing or collected dataset, as long as they report where/how the data was collected.**  
**Participants are not allowed to download the test data from the evaluation server.**

We provide the ML-SUPERB 2.0 public set as a baseline dataset for both training and development (recommended but not required).  
**Data links:** [Google Drive](https://drive.google.com/file/d/1vQ5NksmGl-lY7I4mlU4Kde3EhrEYGii2/view?usp=drive_link), [Huggingface](https://huggingface.co/datasets/espnet/ml_superb_hf)

This baseline dataset is sourced from a variety of multilingual speech corpora (e.g., FLEURS, Common Voice, Multilingual LibriSpeech, etc.), and it covers [141](https://gist.github.com/wanchichen/de008ed6fafca89881a9d0d79b23e58f) of the required [153](https://gist.github.com/wanchichen/5c7f2598e27059accdef3704323a4e84) languages:
- **Training subset:** 1 hour of transcribed speech per language.
- **Development subset:** 10 minutes of transcribed speech per language, suitable for evaluating performance on standard multilingual metrics (i.e., the first four metrics listed at the top of this page).

We also release a development set for various language varieties, which covers 56 dialects and accents (recommended but not required).
**Data links:** [Google Drive](https://drive.google.com/file/d/1E1fBJv6b1rmdHojQVaZ0yHwdw6ETcAw2/view?usp=drive_link) and [Huggingface](https://huggingface.co/datasets/espnet/ml_superb_hf)

Please find detailed descriptions for the data, especially the development data, in the [data description page](/challenge-interspeech2025/data_description).

**Important notes:** 
- We remove **all Norwegian data** ([nno], [nob], [nor]) from evaluation.
- We merge Oriya and Odia data together. Please use [ori] for both.

**Acknowledgement:** we thank Tanel Alumäe for pointing out very important issues which leads to changes mentioned above.

**In summary**:
- Training data:
    - We provide ML-SUPERB 2.0 training subset (recommended but not required).
    - Participants may also use any other existing dataset or collected dataset, as long as they clearly report their sources (some useful resources are listed [here](/challenge-interspeech2025/challenge_overview#Helpful-Resources)).
- Development data:
    - Participants can use the ML-SUPERB 2.0 development set to measure performance (recommended but not required).
    - Participants can also use a development set for various language varieties (recommended but not required).
- More details are presented in the [data description page](/challenge-interspeech2025/data_description).


## Models
Participants are allowed to use any pre-trained model (such as LLMs) or modelling technique they desire.
The only restriction for models is that they:
* Can perform inference without an internet connection.
* Can perform inference within the server's GPU VRAM (~24 GB).

## Baseline model
We provide a [baseline model](https://huggingface.co/espnet/mms_1b_mlsuperb) and [training recipe](https://github.com/espnet/espnet/tree/master/egs2/ml_superb2/asr1) to aid model development. 
The baseline model is an [MMS model](https://www.jmlr.org/papers/volume25/23-1318/23-1318.pdf) with 1 billion parameters, which is fine-tuned on the ML-SUPERB 2.0 training set using [ESPnet](https://github.com/espnet/espnet).
The MMS model is frozen during fine-tuning, and a weighted sum of its layers is fed into a simple 2-layer Transformer encoder, which is trained using the CTC loss. The total number of trainable parameters is 6.36M.
Details about the training configuration can be found [here](https://github.com/espnet/espnet/blob/master/egs2/ml_superb/asr1/conf/tuning/train_asr_fbank_10min.yaml).
The total training time is around 48 hours using a single H100 80GB GPU and 96 hours using a single A100 40GB GPU. 
If you use a smaller pre-trained model, such as [MMS 316M](https://huggingface.co/facebook/mms-300m) or [XLS-R 316M](https://huggingface.co/facebook/wav2vec2-xls-r-300m), the training time is approximately 24 hours on a single A100. 
With CTC greedy search, inference on all of the dev/test sets required around 2.5-3 A100 hours and consumed at most 8GB of VRAM. 
Below are the scores of the MMS baseline on the dev/test sets.

|Model|Standard CER|Standard LID|Worst 15 CER|CER StD|Dialect CER|Dialect LID|
|---|---|---|---|---|---|---|
|MMS 1B CTC|24.0|74.0|71.1|25.5|32.7|54.0|

We also plan to release a stronger baseline system based on [OWSM](https://arxiv.org/abs/2401.16658). Please check back later for details.


## Evaluation and Scoring
Models will be evaluated on two test sets:

* A "standard" multilingual ASR test set.
* A multilingual ASR test set with data from various language varieties.
    
Inference and evaluation will be automatically performed on the server, so participants will not be allowed access to the raw audio nor raw model outputs.
The overall ranking will be determined by a submission's average ranking across six evaluation metrics:

* Average LID accuracy across all 154 languages.
* Average Character Error Rate (CER) across all 154 languages.
* Average CER across the 15 worst-performing languages in the set of 154 languages.
* Standard Deviation of CER computed for each of the 154 languages.
* Average LID across the evaluated language varieties.
* Average CER across the evaluated language varieties.

CER is calculated using unpunctuated upper-case text. Spaces are considered, except on languages that do not have space-delimited word boundaries (Chinese, Japanese, Thai).
Here is the code that we will use for the normalization:

~~~python=
    import re # Python regex
    import string # Python string manipulation library
    import unicodedata # unicode punctuation detection
    from jiwer import cer # CER implementation

    def remove_punctuation(sentence):

        new_sentence = ""
        for char in sentence:
            # all unicode punctuation is of type P
            if unicodedata.category(char).startswith('P'):
                continue
            else:
                new_sentence = f"{new_sentence}{char}"
        return new_sentence

    def normalize_and_calculate_cer(hyp: str, ref: str, remove_spaces: bool):
    
        # remove space for Chinese/Japanese/Thai
        if remove_spaces:
            hyp = re.sub(r"\s", "", hyp)
            ref = re.sub(r"\s", "", ref)

        # remove punctuation
        hyp = remove_punctuation(hyp)
        ref = remove_punctuation(ref)

        # upper case everything
        hyp = hyp.upper()
        ref = ref.upper()

        return cer(hyp, ref)
~~~

The full implementation can be found [here](https://github.com/espnet/espnet/blob/master/egs2/ml_superb2/asr1/local/score.py).
For example, the sentence "I'll be going to the CMU campus." will be converted to "ILL BE GOING TO THE CMU CAMPUS".
If we have a hypothesis from an ASR system that is "ill be going to the see them you campus", it will be converteed to "ILL BE GOING TO THE SEE THEM YOU CAMPUS".
Before normalization, the CER would be 46.85%. After normalization, it would be 33.33%.

For a language that does not use whitespaces, such as Chinese, an example normalization will convert "我想去餐厅 我非常饿" to 我想去餐厅我非常饿".

We will not ask participants to identify the dialect spoken in an utterance (eg. AAVE vs British English), but they are free to use that information in their systems internally.

If a tie-breaker is necessary, we will use the average of the raw metric scores.

The ranking methodology can be summarized as follows:

1. Calculate the rankings for each model on each individual metric.
2. Calculate the average ranking for each model across metrics.
3. Calculate the average of all raw metric scores for tie-breakers (using 1 - LID accuracies, when necessary, such that lower is always better).
3. Rank models by their average ranking.

### Example Ranking

Here is an example ranking based on some baseline models that we trained using the ML-SUPERB 1-hour baseline dataset.
Please note that this example is provided for demonstration purposes only.

#### Raw Scores

| Model | Standard CER | Standard LID | Worst 15 CER | CER StD | Dialect CER | Dialect LID |
|-------|--------------|--------------|--------------|---------|-------------|-------------|
| XEUS           | 22.4 | 77.1 | 78.9 | 30.9 | 23.2 | 79.1 | 
| MMS 1B         | 24.0 | 74.0 | 71.1 | 25.5 | 32.7 | 54.0 |
| w2v-BERT 2.0   | 28.2 | 76.6 | 81.8 | 26.7 | 40.9 | 45.9 |
| XLS-R 128 1B   | 34.2 | 70.9 | 98.3 | 28.9 | 32.7 | 58.1 |
| XLS-R 128 300M | 31.7 | 72.4 | 86.8 | 26.9 | 30.5 | 60.7 |
| XLSR 53        | 38.3 | 63.6 | 93.3 | 26.9 | 23.3 | 71.7 |
| WavLM          | 47.2 | 55.9 | 131.8 | 36.4 | 27.2 | 77.8 |

#### Ranking

| Model | Standard CER | Standard LID | Worst 15 CER | CER StD | Dialect CER | Dialect LID | Avg. Ranking | Final Ranking | 
|-------|--------------|--------------|--------------|---------|-------------|-------------|-------------|-------------|
| XEUS           | 1 | 1 | 2 | 6 | 1 | 1 | 2.0 | 1 |
| MMS 1B         | 2 | 3 | 1 | 1 | 5 | 6 | 3.0 | 2 |
| w2v-BERT 2.0   | 3 | 2 | 3 | 2 | 7 | 7 | 4.0 | 4 |
| XLS-R 128 1B   | 5 | 5 | 6 | 5 | 5 | 5 | 5.1 | 6 |
| XLS-R 128 300M | 4 | 4 | 4 | 3 | 4 | 4 | 3.8 | 3 |
| XLSR 53        | 6 | 6 | 5 | 3 | 2 | 3 | 4.1 | 5 |
| WavLM          | 7 | 7 | 7 | 7 | 3 | 2 | 5.5 | 7 |

## Submission and leaderboard
Participants will upload their API definition and model weights to [DynaBench](https://dynabench.org/tasks/ml-superb), where inference and scoring will be performed automatically.
There is a live leaderboard on [DynaBench](https://dynabench.org/tasks/ml-superb) where we display the rankings of submissions.
A tutorial on submitting to DynaBench can be found [here](https://docs.dynabench.org/docs/dynalab/upload_a_model/).
Example submission scripts can also be found [here](https://github.com/wanchichen/ml_superb_dynabench_submission).
If you have any questions regarding the submission procedure, please ask questions in [our Slack workspace](https://join.slack.com/t/ml-superb/shared_invite/zt-2vwqsru8w-YQaEbAt_4A~nIH8CmDTLzA).

The leaderboard will be frozen for **challenge ranking purposes** on 2/12 11:59AM AoE (12 hours between Interspeech initial submission deadline). 
Inference must be finished before the deadline to appear in the final leaderboard. We expect inference to take 8-24 hours, depending on the submitted model size and architecture. For a 300M/1B CTC model, inference took 15/21 hours respectively.
Each team may submit multiple models. To rank teams against each other, we will only consider the best performing model. Please make sure to include your team details (authors, team name) in the description section of each submission.
Participants may continue to submit after the 2/12 deadline to perform additional ablations and experiments, but those submissions will not affect the final challenge ranking.

Importantly, we allow a maximum of three submissions to the leaderboard per day (this may change in the future depending on the server load).
Please note that all model checkpoints will be deleted from the evaluation server after inference.
We may use the transcriptions generated by the submitted systems for further analyses, but we will not release them without the authors' permission.

## Helpful Resources

* [ML-SUPERB 1.0 ESPnet Recipe](https://github.com/espnet/espnet/tree/master/egs2/ml_superb/asr1).
* ML-SUPERB 1.0 public-set (available on [Google Drive](https://drive.google.com/file/d/1vQ5NksmGl-lY7I4mlU4Kde3EhrEYGii2/view?usp=drive_link) and [Huggingface](https://huggingface.co/datasets/ftshijt/mlsuperb_8th)).
* [List of languages that will be tested](https://gist.github.com/wanchichen/5c7f2598e27059accdef3704323a4e84).
* [List of languages in ML-SUPERB 1.0](https://gist.github.com/wanchichen/de008ed6fafca89881a9d0d79b23e58f).
* [List of languages that will be tested that are NOT in ML-SUPERB 1.0](https://gist.github.com/wanchichen/d13e2cb6f7bb773efc256319c4d383ef).
* Example list of speech foundation models:
    * [S3PRL SSL List](https://s3prl.github.io/s3prl/tutorial/upstream_collection.html)
    * [MMS 1B](https://huggingface.co/facebook/mms-1b)
    * [XEUS](https://www.wavlab.org/activities/2024/xeus/)
    * [w2v-BERT](https://huggingface.co/facebook/w2v-bert-2.0)
    * [Whisper](https://huggingface.co/openai/whisper-large-v3)
    * [OWSM](https://www.wavlab.org/activities/2024/owsm/)
* Example list of multilingual speech datasets:
    * [YODAS](https://huggingface.co/datasets/espnet/yodas)
    * [Emilia](https://huggingface.co/datasets/amphion/Emilia-Dataset)
    * [FLEURS](https://huggingface.co/datasets/amphion/Emilia-Dataset)
    * [MLS](https://huggingface.co/datasets/facebook/multilingual_librispeech)
    * [XEUS unlab](https://www.wavlab.org/activities/2024/xeus/)
* [DynaBench Submission Tutorial](https://docs.dynabench.org/docs/dynalab/upload_a_model/).
* [DynaBench Submission Template](https://github.com/mlcommons/dynabench/tree/7731ccbae0f8cdb9557517669f3bd11ecae9e13d/backend/dynalab).
    

## Rules

* Inference and scoring must be performed on the evaluation server.
* Do not attempt to download the test data (or its derivatives) from the evaluation server.
* Model inference must be performed without connection to the internet.
* Model inference must be able to be performed within the GPU memory constraints of the server. 
* There are no constraints about the inference runtime, but we reserve the right to terminate inference jobs that are excessively long.
* Multiple submissions to the evaluation server are allowed.
* Only one submission from each team will be considered for the final ranking.
* There is a maximum of three submissions to the leaderboard per day. This is subject to change depending on server load.
* Individuals can be a part of multiple teams. 
* Organizers and technical committee members are allowed to participate in the challenge.


## Organizers

William Chen (CMU)

Jiatong Shi (CMU)

Shih-Heng Wang (CMU)

Shinji Watanabe (CMU)

Antonios Anastasopoulos (GMU)

Chutong Meng (GMU)

Martijn Bartelds (Stanford)

Dan Jurafsky (Stanford)

Hung-yi Lee (NTU)

Hsiu-Hsuan Wang (NTU)

Karen Livescu (TTIC)

# Contact

[superb.announcement@gmail.com](mailto:superb.announcement@gmail.com)
`;

const framework = String.raw`
## Evaluation Framework

Please check https://github.com/espnet/espnet/tree/master/egs2/ml_superb/asr1#readme for details.

`;

const upstream = String.raw`
## Upstream Specification

### Unlabeled data only: Focus on SSL

- Any labeled/parallel data made by human annotators are **not allowed** to used for both model training and data preprocessing, e.g.
    - **audio/text pairs:** transcriptions in English, foreign languages, or phonemes.
    - **audio/tagging pairs:** speaker labels or sound event labels.
    - **audio/audio pairs:** audios with the same properties made parallel by human, e.g. audios with same content from different speakers, or the opposite.
- Any system pre-trained by labeled/parallel data **cannot** be used to help with the SSL pre-training, like pre-trained ASR.
- Any unlabeled/unparallel data is allowed, including the downstream datasets in the public-set. The nature alignments (not made by human annotators) between audio and other modalities are also allowed, e.g. videos.
- If it is hard to define whether your data is labeled/parallel, please [contact us](/challenge-slt2022/challenge_overview#Contact)!

### Programming Language

- We currently support:
    - **Python >= 3.6**
    - **Pytorch >= 1.7**

- We expect the upstream submission can pass the following check:
    ~~~python=
    upstream = YourModel.cuda()
    assert isinstance(upstream, torch.nn.Module)
    ~~~


We accept upstream models in PyTorch by default. If you wish to submit upstreams in non-PyTorch frameworks, or are not feasible to submit the pre-trained model, please [contact us](/challenge-slt2022/challenge_overview#Contact) for us to see how to help!

### Interface functions

See [here](https://huggingface.co/superb/superb-submission#1-add-model-interfaces).
`;

const submission = String.raw`
## Public-set

### As the task definition and demonstration

The public-set serves as the demonstration of the task design, including:
* the data preprocessing,
* tasks' input/output formats,
* task-specific metrics. 

The datasets used in the public-set are all chosen to be public available for everyone to participate. Please refer to [TASKS](/tasks) and the implementation in [S3PRL](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb.md) for details.

### Provide baselines for comparison

#### Baselines

We collected most of the well-known SSL baseline models in [S3PRL](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb.md), including TERA, wav2vec 2.0, HuBERT, DeCoAR 2.0, WavLM, LightHuBERT, and more. You can easily benchmark different upstreams by specifying in the command line arguments.

#### Comparison

Since the full benchmarking on the public-set can take some time for the training to converge. We released the [training artifacts](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb_artifacts.md) of the top baseline systems (e.g. wav2vec2, HuBERT, WavLM) for participants to quickly compare with them. The artifacts include:

- Tensorboard logs
- Trained downstream weights (the best on public dev set)

### Public-set leaderboard and submission

The [public-set leaderboard](/leaderboard?subset=Public+Set&track=constrained) is online and [accepts submissions](/submit?type=public) (need login). There is no deadline for it. Since all the train/dev/test splits are public available, **the leaderboard accepts submissions with the inferenced prediction files on each task's testing split** which will be auto-generated if you follow the benchmarking steps in [S3PRL](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb.md). And in this challenge, we only consider submissions submited to the **Constrained track** (see [Rules](/rules) for further information about track).

## Hidden-set

We use Hugging Face as the platform for model submission, and here is the [submit tutorial]((https://huggingface.co/superb/superb-submission)).

### Fairness & prevent overfitting

Since all the train/dev/test splits are public in the public-set, it is possible to cheat by directly reporting the best results on the testing split, and the results are thus overfit on the testing split. Hence, the hidden-set is collected and prepared to follow the same task design as that in the public-set but with the newly created data. All the splits will **NOT be released in both audio and labels**. The members involved in the hidden-set preparation should **NOT** participate the challenge. These members are listed in the [**Hidden-set Committee**](/challenge-slt2022/challenge_overview#Hidden-set-Committee) below.

### Hidden-set leaderboard and submission

#### Submission type

The leaderboard accepts **submissions with the upstream model solely**, including **model definition** and **pre-trained weights**. The upstream model should follow the specification detailed at [Upstream Specification](/challenge-slt2022/upstream). The submission can be done publicly or privately. Only the [**Hidden-set Committee**](/challenge-slt2022/challenge_overview#Hidden-set-Committee) members can access the privately submitted upstreams and the models will be used solely for this challenge.

- We accept upstream models in PyTorch by default. If you wish to submit upstreams in non-PyTorch frameworks, please [contact us](/challenge-slt2022/challenge_overview#Contact)!
- If you are not feasible to submit the pre-trained model, please [contact us](/challenge-slt2022/challenge_overview#Contact) for us to see how to help!

#### Finetuning on submission

After the upstream model is submitted, we **benchmark the submitted upstream by finetuning each task's downstream model for participants**. **The quota for submissions per week is limited and starts from 2 times/week,** but will be dynamically adjusted based on the number of participants. The quota adjustment will be announced at [NEWS](https://superbbenchmark.org/news). Participants can [contact us](/challenge-slt2022/challenge_overview#Contact) to acquire the finetuning artifacts of their own submissions for sanity checks, including:

- Tensorboard logs
- Testing results
- Trained downstream weights

#### Private dev/test scores

After training the downstream model for all tasks with several different learning rates, we will show the best performance of each task on the hidden-set's development splits. And show the true performance from the same checkpoint on the testing splits.

#### How to submit

The [hidden-set leaderboard](/leaderboard?subset=Hidden+Dev+Set&track=constrained) is online and accepts submissions. Please follow the submission steps. We use HuggingFace's Hub to host the submitted upstreams and track the submitted model weights. On the other hand, we use our submission page to control the submission limit, where the participants tell us the locations of their models on HuggingFace's Hub.

After submitting via the submission page, you will see a new entry in the submission history under your Profile page, and the evaluation results on the hidden-set will be revealed at the entry within one week. Please stay tuned!

All the submitted results will remain anonymous during and after the challenge. You can select a few submissions to show on the leaderboard. If no submission is selected, we will show all your submissions on the leaderboard anonymously. Only you can see your personal/model information on the leaderboard, but you will not see others'. If you wish to reveal your personal/model information on the hidden-set leaderboard, please contact us!

## Profiling Tool

(Update 04/28/2022 AOE)

[The profiling tool](https://github.com/B06901052/DeepSpeed/tree/superb-challenge) we used are mainly based on [**DeepSpeed**](https://github.com/microsoft/DeepSpeed/tree/master/deepspeed/profiling/flops_profiler) developed by **Microsoft**. And for our demands, we fork it and add some additional features. You can use it to profile your model or upstreams in s3prl. All the details are in the repo's README.md.

To match reality, we choose 32 audios as model inputs from LibriSpeech test-clean split by:
1. sort all audios by sequence length
2. choose (82*i + 1)th from i=0~31 audios (2620 audios in total)
`;

const metrics = String.raw`
## Overall Metrics

(Update 04/09/2022 AOE)

In this challenge, there are two types of metrics, performance and efficiency metrics.

For performance metrics, we announce two kinds of metrics to measure it: **superb-rank** ($superb_r$) and **superb-score** ($superb_s$) in parameter-agnostic manner. In this challenge, $superb_r$ is the primary metrics. When equal rank is found on two different upstreams, $superb_{s}$ is used to break the tie.

To encourage the development of speech SSL on small and green models, we add two metrics to measure model efficiency, **Params** for memory usage and **MACs** for computataional cost as another targets to make improvement. Also, to gauge the effectiveness and parameter-efficiency of new SSL algorithms, we encourage participants to submit multiple upstreams trained by the same algorithm with the only difference in parameter size. They are computed by [this profiling tool](/challenge-slt2022/submission#Profiling-Tool).

### Notation

Suppose each task $t$ in all tasks $T$ has a single metric $s_t$, and the score of an upstream $u$ on task $t$ is $s_t(u)$ which has already been transformed to make higher values represent better performance. Eg. We use WAcc here for ASR instead of the raw WER.

If there are two or more metrics in one task $T$, such as pesq and stoi in SE, then the $s_t$ will be the arithmetic mean of them.

### Performance Metrics

Performance metrics demonstrate the best performance SSL can achieve, and encourages participants to explore any possibility to push the limits.

#### SUPERB$_s$

To aggregate all task-specific scores $s_t(u)$ into a single static score, we linearly transform each of them into points so that:

- $s_t(fbank) = 0$, The performance of FBANK maps to 0
- $s_t(sota) = 1000$, The performance of the existing SOTA upstream *for this task* maps to 1000.

$$
p_t(u) = \dfrac{1000}{s_t({sota}) - s_t({fbank})}\ (\ s_t(u) - s_t({fbank})\ )
$$

Hence, most of the points will sit between 0~1000. The upstream worse than FBANK on this task will get negative points. The upstream better than the task-specific SOTA upstream will get points higher than 1000. The $superb_s$ of the upstream $u$ is the average of $p_t$ over all tasks.

$$
\text{SUPERB}_s = \dfrac{1}{|T|} \sum_{t \in T}\ p_t(u)
$$

Intuitively, two reference points: FBANK and SOTA decide the typical interested interval for a task-specific metric and scale the task scores to 0~1000 points accordingly. The similar range of points across tasks can then be averaged. Beyond scaling with the pre-defined metric range, this interval further determines *how hard for a task to improve*. For a harder task, its smaller interval at the denominator give the task more credit for any unit improvement.


### Efficiency Metrics

#### MACs (number of Multiply-ACcumulate operations)

For this metrics, we are focus on the algorithm-level, rather than software-level or hardware-level, so the cost yeild from the real implementation will not be taken into consideration. Take the torch.Tensor.\_\_rdiv\_\_ as an example, if its input is int or float, it will call reciprocal, \_\_mul\_\_, and \_\_truediv\_\_, respectively, but we still count it only once. Also, when a function is counted, those functions called in it will not be counted again.

##### Estimated Rule

The original defination of MACs should be a multiply operation followed by an add operation, as below:

$MACs=num(a\times b + c)$

But in the implementation of the profiling tool we used (most done by DeepSpeed, Microsoft), the value may be approximated by following formulas or something similar:

$\approx\max\{num(add)+num(sub), num(mul)+num(div)\}$

$\approx FLOPs/2$

##### Ignored Modules

For the modules below, it will not be taken into account and will raise a warning message when your model forwards a function in them.

- torch.special
- torch.fft
- torch.linalg
- torchaudio
- torchvision

In this challenge, we consider not including those modules since they are usually used as preprocessing (fft, torchaudio) or rarely used (special, linalg). For preprocessing, it maybe has been done by other modules or simply loads the preprocessed data, so we think it will be much fairer if we exclude those computation. And for those modules rarely used, we ignore them due to the time cost. If you think we should add some functions in them into account, feel free to [contact us](/challenge-slt2022/challenge_overview#Contact) or PR your formulas for those functions to [the profiling tool](https://github.com/B06901052/DeepSpeed/tree/superb-challenge). 

#### Params (number of Parameters)

- The total number of parameters in your upstream model using in any downstream task.

### Conclusion

The ranking metrics are the primary performance measures in this challenge and are designed to emphasize an upstream's universally usability. When two upstreams tie on the same rank, the scoring metrics take the tasks' variations and improvement difficulty into account to help the final decision. You can refer to the [public-set leaderboard](/leaderboard?subset=Public+Set&track=constrained) for the overall metrics calculation.
`;

export { challenge_overview };
// using String.raw to avoid escaping in latex expression
// to meet the parsing format, do things as below
// remove comment <!-- ...  -->
// replace ``` with ~~~ (code block)
// replace <strong> with **
// replace math block below $superb_{sp}$ as another ver. (origin ver. can't be render but the reason is unknown)
